/**
 * Which dashboard widgets a reader sees: the role's widgets narrowed by the
 * plan, the permission layer and the company's disabled modules, then
 * narrowed and reordered by the company default and by the user's own
 * choice.
 *
 * Every layer after the role's can only remove or reorder; none adds. A
 * key a selection names that the layer above did not leave is ignored.
 */
import { DASHBOARD_WIDGETS } from "./widgets.js";
/** The `UiPreference` key of the company default and of the user's choice. */
export const DASHBOARD_WIDGETS_PREFERENCE_KEY = "dashboard.widgets";
export function isDashboardWidgetKey(key) {
    return typeof key === "string" && Object.hasOwn(DASHBOARD_WIDGETS, key);
}
/**
 * A stored or submitted selection as a list of catalogue keys: unknown
 * keys and repeats dropped, order kept. Anything but an array is empty.
 */
export function sanitizeWidgetSelection(raw) {
    if (!Array.isArray(raw))
        return [];
    const kept = [];
    for (const key of raw) {
        if (isDashboardWidgetKey(key) && !kept.includes(key))
            kept.push(key);
    }
    return kept;
}
/** What a reader may see at all: role, plan, permission, company modules. */
export function availableWidgets(input) {
    return input.roleWidgets.filter((key) => {
        const widget = DASHBOARD_WIDGETS[key];
        if (widget === undefined)
            return false;
        const { planFeature, permission } = widget;
        if (planFeature !== undefined && input.planFeatures !== null && !input.planFeatures.includes(planFeature)) {
            return false;
        }
        if (permission !== null && !input.can(permission))
            return false;
        return input.isModuleDisabled?.(key) !== true;
    });
}
/**
 * `selection` applied to `allowed`: only the keys of `allowed` it names, in
 * its order. A selection that is empty, or names none of `allowed`, leaves
 * `allowed` as it is, so a stale choice never blanks the page.
 */
export function narrowWidgets(allowed, selection) {
    const chosen = sanitizeWidgetSelection(selection).filter((key) => allowed.includes(key));
    return chosen.length > 0 ? chosen : [...allowed];
}
export function resolveDashboardWidgets(input) {
    const allowed = availableWidgets(input);
    return narrowWidgets(narrowWidgets(allowed, input.company), input.user);
}
//# sourceMappingURL=widget-selection.js.map